import { useLayoutEffect, useRef, type JSX, type UIEvent } from 'react';

export interface SubtitleLine {
  readonly id: number;
  readonly text: string;
}

interface ScrollPosition {
  readonly top: number;
  readonly offsets: ReadonlyMap<string, number>;
}

function readScrollPosition(panel: HTMLDivElement): ScrollPosition {
  return {
    top: panel.scrollTop,
    offsets: new Map(
      Array.from(panel.children, (child) => {
        const entry = child as HTMLDivElement;
        return [entry.dataset.subtitleId ?? '', entry.offsetTop];
      }),
    ),
  };
}

/** Keep captions bounded without discarding the text someone is reading. */
export function SubtitlePanel({
  lines,
  label,
}: {
  readonly lines: readonly SubtitleLine[];
  readonly label: string;
}): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const followLatestRef = useRef(true);
  const positionRef = useRef<ScrollPosition | null>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      followLatestRef.current = true;
      positionRef.current = null;
      return;
    }

    if (followLatestRef.current) {
      panel.scrollTop = panel.scrollHeight;
    } else {
      // The stage retains the latest five turns. If an older turn was removed,
      // keep a surviving turn at the same visual position instead of jumping
      // to the newest answer or shifting the reader down by the removed text.
      const previous = positionRef.current;
      if (previous) {
        for (const child of panel.children) {
          const entry = child as HTMLDivElement;
          const previousOffset = previous.offsets.get(entry.dataset.subtitleId ?? '');
          if (previousOffset === undefined) continue;
          panel.scrollTop = Math.max(0, previous.top + entry.offsetTop - previousOffset);
          break;
        }
      }
    }
    positionRef.current = readScrollPosition(panel);
  }, [lines]);

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    const panel = event.currentTarget;
    followLatestRef.current = panel.scrollHeight - panel.clientHeight - panel.scrollTop <= 4;
    positionRef.current = readScrollPosition(panel);
  };

  if (lines.length === 0) return null;

  return (
    <div
      ref={panelRef}
      className="agent-subtitles"
      role="log"
      aria-label={label}
      aria-live="polite"
      aria-relevant="additions"
      aria-atomic="false"
      tabIndex={0}
      data-embed-subtitle=""
      onScroll={handleScroll}
    >
      {lines.map((line, index) => (
        <div
          key={line.id}
          data-subtitle-id={line.id}
          className="agent-subtitle-entry"
          style={{ opacity: index === lines.length - 1 ? 1 : 0.7 }}
        >
          {line.text}
        </div>
      ))}
    </div>
  );
}
